import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from 'react-query';
import { message } from 'antd';
import {
  HomeOutlined,
  PlusOutlined,
  BarChartOutlined,
  MenuOutlined,
  CloseOutlined,
  UserOutlined,
  ShopOutlined,
  ExclamationCircleOutlined,
  WalletOutlined,
  TagsOutlined,
  PieChartOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import { ExpenseTransactionModalMobile } from '../expenses/ExpenseTransactionModalMobile';
import { AddCategoriesModalMobile } from '../expenses/AddCategoriesModalMobile';
import { useAuthStore } from '../../stores/authStore';
import { Button } from 'antd';
import Logo from './Logo';

const BottomNavBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, logout } = useAuthStore();

  // States
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showMorePopover, setShowMorePopover] = useState(false);
  const [showReportDrawer, setShowReportDrawer] = useState(false);

  const moreMenuRef = useRef<HTMLButtonElement>(null);

  // Queries

  // Handlers

  const handleCategorySuccess = async () => {
    try {
      await queryClient.invalidateQueries('expense-categories');
      setShowCategoryModal(false);
      message.success('Tạo danh mục chi tiêu thành công');
      // Refresh màn hình gốc sau khi thêm thành công
      setTimeout(() => {
        window.location.reload();
      }, 100);
    } catch (error) {
      message.error('Có lỗi xảy ra khi cập nhật dữ liệu');
    }
  };

  // Đóng popover khi click ra ngoài
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(e.target as Node)) {
        setShowMorePopover(false);
      }
    };
    if (showMorePopover) {
      document.addEventListener('click', handleClick);
    }
    return () => document.removeEventListener('click', handleClick);
  }, [showMorePopover]);

  // Xác định active tab dựa vào current path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'dashboard';
    if (path.includes('/expenses')) return 'expenses';
    if (path.includes('/gifts')) return 'gifts';
    if (path === '/reports') return 'report';
    if (path === '/system') return 'more';
    return '';
  };

  const activeTab = getActiveTab();

  const buttonStyle = {
    pointerEvents: 'auto' as const,
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    background: 'none',
    border: 'none',
    padding: '4px 0',
    cursor: 'pointer',
    minWidth: '48px',
    transition: 'color 0.3s ease'
  };

  const iconStyle = (isActive: boolean) => ({
    fontSize: '24px',
    marginBottom: '4px',
    color: isActive ? '#1890ff' : '#999'
  });

  const labelStyle = (isActive: boolean) => ({
    fontSize: '12px',
    marginTop: '2px',
    color: isActive ? '#1890ff' : '#999'
  });

  const handleCreateNew = (type: string) => {
    setShowCreateModal(false);
    switch (type) {
      case 'expense':
        setShowExpenseModal(true);
        break;
      case 'category':
        setShowCategoryModal(true);
        break;
    }
  };

  const createMenuItems = [
    { 
      title: 'Tạo mới Giao dịch chi tiêu',
      type: 'expense',
      icon: <WalletOutlined style={{ fontSize: '20px', color: '#52c41a' }} />,
      color: '#f6ffed',
      borderColor: '#b7eb8f'
    },
    { 
      title: 'Tạo mới Danh mục chi tiêu',
      type: 'category',
      icon: <TagsOutlined style={{ fontSize: '20px', color: '#722ed1' }} />,
      color: '#f9f0ff',
      borderColor: '#d3adf7'
    }
  ];

  const moreMenuItems: Array<{
    title: string;
    icon: React.ReactElement;
    path: string;
  }> = [
    {
      title: 'Danh mục chi tiêu',
      icon: <TagsOutlined style={{ fontSize: '20px', color: '#722ed1' }} />,
      path: '/expenses/categories'
    }
  ];



  const expenseMenuItems = [
    {
      title: 'Giao dịch chi tiêu',
      icon: <WalletOutlined style={{ fontSize: '20px', color: '#52c41a' }} />,
      path: '/expenses'
    },
    {
      title: 'Danh mục chi tiêu',
      icon: <TagsOutlined style={{ fontSize: '20px', color: '#722ed1' }} />,
      path: '/expenses/categories'
    },
    {
      title: 'Báo cáo chi tiêu',
      icon: <PieChartOutlined style={{ fontSize: '20px', color: '#fa8c16' }} />,
      path: '/expenses/reports'
    }
  ];

  // Danh sách báo cáo
  const reportMenuItems = [
    {
      title: 'Báo cáo doanh thu',
      path: '/reports/revenue',
    },
    {
      title: 'Báo cáo nhập-xuất-tồn',
      path: '/reports/stock-in-out',
    },
    {
      title: 'Báo cáo chi tiêu',
      path: '/expenses/reports',
    },
  ];

  // Logo phần mềm
  const getLogo = () => (
    <div style={{
      width: 40,
      height: 40,
      borderRadius: '50%',
      overflow: 'hidden',
      background: '#fff',
      marginRight: -80,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <Logo size="medium" />
    </div>
  );

  return (
    <>
      <nav style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        background: '#fff',
        borderTop: '1px solid #f0f0f0',
        height: '96px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        paddingBottom: 'env(safe-area-inset-bottom, 24px)',
        pointerEvents: 'none'
      }}>
        {/* Dashboard */}
        <button
          style={buttonStyle}
          onClick={() => navigate('/dashboard')}
        >
          <HomeOutlined style={iconStyle(activeTab === 'dashboard')} />
          <span style={labelStyle(activeTab === 'dashboard')}>Dashboard</span>
        </button>

        {/* Tiền mừng */}
        <button
          style={buttonStyle}
          onClick={() => navigate('/gifts')}
        >
          <GiftOutlined style={iconStyle(activeTab === 'gifts')} />
          <span style={labelStyle(activeTab === 'gifts')}>Tiền mừng</span>
        </button>

        {/* Nút tạo mới ở giữa */}
        <div style={{ ...buttonStyle, position: 'relative' }}>
          <button
            style={{
              pointerEvents: 'auto',
              position: 'absolute',
              top: '-68px', // Đẩy nút lên cao hơn khi menu cao hơn
              left: '50%',
              transform: 'translateX(-50%)',
              width: '76px',
              height: '76px',
              borderRadius: '50%',
              background: '#1890ff',
              border: '4px solid #fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
              color: '#fff',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              zIndex: 1 // Thêm để đảm bảo nút luôn nổi lên trên
            }}
            onClick={() => setShowCreateModal(true)}
          >
            <PlusOutlined style={{ fontSize: '28px' }} />
          </button>
        </div>

        {/* Chi tiêu */}
        <button
          style={buttonStyle}
          onClick={() => navigate('/expenses')}
        >
          <WalletOutlined style={iconStyle(activeTab === 'expenses')} />
          <span style={labelStyle(activeTab === 'expenses')}>Chi tiêu</span>
        </button>



        {/* Khác */}
        <button
          ref={moreMenuRef}
          style={buttonStyle}
          onClick={() => setShowMorePopover(prev => !prev)}
        >
          <MenuOutlined style={iconStyle(activeTab === 'more' || showMorePopover)} />
          <span style={labelStyle(activeTab === 'more' || showMorePopover)}>Khác</span>
        </button>
      </nav>



      {/* Popover cho menu Khác */}
      {showMorePopover && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'flex-end'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowMorePopover(false);
          }}
        >
          <div style={{
            width: '100%',
            background: '#fff',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            maxWidth: '500px',
            margin: '0 auto',
            overflow: 'hidden'
          }}>
            {/* User info + logout */}
            <div style={{
              padding: '16px',
              borderBottom: '1px solid #f0f0f0',
              background: '#fafbfc',
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              justifyContent: 'space-between',
            }}>
              {getLogo()}
              <div style={{ marginLeft: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                <div style={{ fontWeight: 700, fontSize: 17, color: '#262626', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.full_name || user?.username}</div>
                <div style={{ fontSize: 13, color: '#888', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.username}</div>
              </div>
              <Button
                type="primary"
                danger
                size="small"
                style={{ borderRadius: 6, fontWeight: 600 }}
                onClick={() => { logout(); navigate('/login'); }}
              >
                Đăng xuất
              </Button>
            </div>

            <div style={{ 
              padding: '16px',
              display: 'flex', 
              flexDirection: 'column', 
              gap: '8px',
              maxHeight: 'calc(85vh - 64px)',
              overflowY: 'auto',
              paddingBottom: 40
            }}>
              {moreMenuItems.map((item) => (
                <button
                  key={item.path}
                  style={{
                    width: '100%',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: '#fff',
                    border: '1px solid #f0f0f0',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                  }}
                  onClick={() => {
                    navigate(item.path);
                    setShowMorePopover(false);
                  }}
                >
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '8px',
                    background: item.icon.props.style.color + '15',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {React.cloneElement(item.icon, {
                      style: { 
                        ...item.icon.props.style,
                        fontSize: '20px'
                      }
                    })}
                  </div>
                  <span style={{ 
                    flex: 1,
                    textAlign: 'left',
                    fontSize: '15px',
                    fontWeight: 500,
                    color: '#000000d9'
                  }}>{item.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modal tạo mới */}
      {showCreateModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'flex-end'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowCreateModal(false);
          }}
        >
          <div style={{
            width: '100%',
            background: '#fff',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            maxWidth: '500px',
            margin: '0 auto',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #f0f0f0',
              background: '#fff'
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '18px', 
                fontWeight: 600,
                color: '#000000d9'
              }}>Tạo mới</h3>
              <button
                style={{
                  width: '32px',
                  height: '32px',
                  background: '#f5f5f5',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => setShowCreateModal(false)}
              >
                <CloseOutlined style={{ fontSize: '16px', color: '#00000073' }} />
              </button>
            </div>
            <div style={{ 
              padding: '8px',
              display: 'flex', 
              flexDirection: 'column', 
              gap: '8px',
              background: '#fff',
              paddingBottom: 40,
            }}>
              {createMenuItems.map((item) => (
                <button
                  key={item.type}
                  style={{
                    width: '100%',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: item.color,
                    border: `1px solid ${item.borderColor}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => handleCreateNew(item.type)}
                >
                  {item.icon}
                  <span style={{ 
                    flex: 1,
                    textAlign: 'left',
                    fontSize: '15px',
                    fontWeight: 500,
                    color: '#000000d9'
                  }}>{item.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

             {/* Các Modal tạo mới */}
             <ExpenseTransactionModalMobile
         isOpen={showExpenseModal}
         onClose={() => setShowExpenseModal(false)}
         onSuccess={() => {
           setShowExpenseModal(false);
           message.success('Thêm giao dịch chi tiêu thành công');
           // Chuyển về trang expenses sau khi thêm thành công
           navigate('/expenses');
         }}
       />

      <AddCategoriesModalMobile
        isOpen={showCategoryModal}
        onClose={() => setShowCategoryModal(false)}
        onSuccess={handleCategorySuccess}
      />

      {/* Drawer danh sách báo cáo */}
      {showReportDrawer && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1001,
            display: 'flex',
            alignItems: 'flex-end'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowReportDrawer(false);
          }}
        >
          <div style={{
            width: '100%',
            background: '#fff',
            borderTopLeftRadius: '16px',
            borderTopRightRadius: '16px',
            maxWidth: '500px',
            margin: '0 auto',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '16px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderBottom: '1px solid #f0f0f0',
              background: '#fff'
            }}>
              <h3 style={{ 
                margin: 0, 
                fontSize: '18px', 
                fontWeight: 600,
                color: '#000000d9'
              }}>Danh sách báo cáo</h3>
              <button
                style={{
                  width: '32px',
                  height: '32px',
                  background: '#f5f5f5',
                  border: 'none',
                  borderRadius: '50%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
                onClick={() => setShowReportDrawer(false)}
              >
                <CloseOutlined style={{ fontSize: '16px', color: '#00000073' }} />
              </button>
            </div>
            <div style={{ 
              padding: '16px',
              display: 'flex', 
              flexDirection: 'column', 
              gap: '12px',
              background: '#fff',
              paddingBottom: 40
            }}>
              {reportMenuItems.map((item) => (
                <button
                  key={item.path}
                  style={{
                    width: '100%',
                    padding: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    background: '#f6f8fa',
                    border: '1px solid #e6e6e6',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '16px',
                    fontWeight: 500,
                    color: '#262626',
                    transition: 'all 0.3s',
                  }}
                  onClick={() => {
                    navigate(item.path);
                    setShowReportDrawer(false);
                  }}
                >
                  <BarChartOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                  <span style={{ flex: 1, textAlign: 'left' }}>{item.title}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BottomNavBar; 